import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import { 
  BarChart3, Users, FileText, Mail, Shield, LogOut, 
  Calendar, MapPin, RefreshCw, ArrowLeft, Menu, X,
  Gamepad2, Plus, Search, Building2, Trophy, Star, BookOpen, 
  Megaphone, Smartphone, Award
} from "lucide-react";

// Navigation items with two-line labels
const NAV_ITEMS = [
  { id: "how-it-works", labelTop: "How It", labelBottom: "Works", icon: Gamepad2 },
  { id: "host-game", labelTop: "Host a", labelBottom: "Game", icon: Plus },
  { id: "find-games", labelTop: "Find", labelBottom: "Games", icon: Search },
  { id: "featured", labelTop: "Featured", labelBottom: "Games", icon: Trophy },
  { id: "membership", label: "Membership", icon: Star },
  { id: "discover", label: "Players", icon: Users, isPage: true, path: "/discover" },
  { id: "leaderboard", label: "Leaderboard", icon: Award, isPage: true, path: "/leaderboard" },
  { id: "rules", label: "Rules", icon: BookOpen, isPage: true, path: "/rules" },
  { id: "sponsors", label: "Sponsors", icon: Megaphone, isPage: true, path: "/sponsors" },
  { id: "venues", label: "Venues", icon: Building2, isPage: false },
  { id: "apps", label: "Apps", icon: Smartphone, isPage: true, path: "/apps" },
  { id: "contact", label: "Contact", icon: Mail, isPage: true, path: "/contact" },
];

const CRMPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, adminLogin, logout } = useAuth();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("analytics");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [ndaSubmissions, setNdaSubmissions] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [adminLogins, setAdminLogins] = useState([]);

  useEffect(() => {
    if ((user?.role === "crm_admin" || user?.role === "admin") && token) {
      setIsLoggedIn(true);
      fetchData();
    }
  }, [user, token]);

  const fetchData = async () => {
    if (!token) {
      console.warn("CRM fetchData called without token");
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [analyticsRes, ndaRes, contactRes, usersRes, loginsRes] = await Promise.all([
        axios.get(`${API}/crm/analytics`, { headers }),
        axios.get(`${API}/crm/nda-submissions`, { headers }),
        axios.get(`${API}/crm/contact-submissions`, { headers }),
        axios.get(`${API}/crm/users`, { headers }),
        axios.get(`${API}/crm/admin-logins`, { headers }),
      ]);

      setAnalytics(analyticsRes.data);
      setNdaSubmissions(ndaRes.data.submissions || []);
      setContactSubmissions(contactRes.data.submissions || []);
      setUsers(usersRes.data.users || []);
      setAdminLogins(loginsRes.data.logins || []);
    } catch (err) {
      console.error("CRM fetch error:", err);
      toast.error("Failed to fetch CRM data");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await adminLogin(password, "crm");
      setIsLoggedIn(true);
      toast.success("CRM access granted");
      fetchData();
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    navigate("/");
  };

  const handleNavClick = (item) => {
    if (item.isPage) {
      navigate(item.path);
    } else {
      navigate("/home", { state: { section: item.id } });
    }
    setMobileMenuOpen(false);
  };

  if (!isLoggedIn) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "#050505" }}
        data-testid="crm-login-page"
      >
        <Card className="w-full max-w-sm bg-[#0A0A0A] border border-[#7a6200]/30">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                <BarChart3 className="w-7 h-7 text-[#7a6200]" />
              </div>
              <h1 className="font-bebas text-2xl tracking-wider text-white">
                CRM ACCESS
              </h1>
              <p className="text-[#52525B] text-sm font-ubuntu mt-1">
                Board2Boston™ Admin Panel
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter CRM password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark h-12 text-center"
                data-testid="crm-password-input"
                autoFocus
              />

              <Button
                type="submit"
                className="btn-gold w-full h-12"
                disabled={loading || !password}
                data-testid="crm-login-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "ACCESS CRM"
                )}
              </Button>
            </form>

            <button 
              onClick={() => navigate(-1)}
              className="w-full mt-4 text-[#52525B] text-sm font-ubuntu hover:text-[#A1A1AA] transition-colors"
            >
              ← Back
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="crm-dashboard">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#050505]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Left section: Back, Logo, Title */}
            <div className="flex items-center gap-4">
              {/* Back Button */}
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-white/10 text-[#A1A1AA] hover:text-white transition-colors"
                data-testid="crm-back-btn"
                title="Go Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              {/* Logo - 120% larger (52px * 1.2 = 62.4px) */}
              <img
                src="https://customer-assets.emergentagent.com/job_hoop-invite/artifacts/r8lcaocz_Board2Boston%20favicon.png"
                alt="Board2Boston"
                style={{ height: '87px' }}
                className="cursor-pointer"
                onClick={() => navigate("/home")}
              />
              <div>
                <h1 className="font-bebas text-xl tracking-wider text-white">CRM DASHBOARD</h1>
                <p className="text-[#52525B] text-xs font-ubuntu">Administrative Panel</p>
              </div>
            </div>

            {/* Center section: Navigation (Desktop) */}
            <nav className="hidden xl:flex items-center" style={{ gap: '2px' }}>
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className="px-2 py-1 font-ubuntu text-xs transition-colors text-[#A1A1AA] hover:text-white text-center leading-tight whitespace-nowrap"
                  data-testid={`crm-nav-${item.id}`}
                >
                  {item.labelTop ? (
                    <span className="flex flex-col items-center">
                      <span>{item.labelTop}</span>
                      <span>{item.labelBottom}</span>
                    </span>
                  ) : (
                    item.label
                  )}
                </button>
              ))}
            </nav>
            
            {/* Right section: Actions */}
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={fetchData}
                className="text-[#A1A1AA] hover:text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="text-[#A1A1AA] hover:text-white"
                data-testid="crm-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>

              {/* Mobile Menu Toggle */}
              <button
                className="xl:hidden text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="xl:hidden border-t border-white/10 bg-[#0A0A0A]">
            <nav className="px-4 py-4 space-y-2">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded font-ubuntu text-sm text-[#A1A1AA] hover:bg-white/5"
                >
                  <item.icon className="w-5 h-5" />
                  {item.labelTop ? `${item.labelTop} ${item.labelBottom}` : item.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 pt-28">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#0A0A0A] border border-white/10 mb-8">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#7a6200]">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#7a6200]">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="nda" className="data-[state=active]:bg-[#7a6200]">
              <FileText className="w-4 h-4 mr-2" />
              NDA Submissions
            </TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-[#7a6200]">
              <Mail className="w-4 h-4 mr-2" />
              Contact
            </TabsTrigger>
            <TabsTrigger value="logins" className="data-[state=active]:bg-[#7a6200]">
              <Shield className="w-4 h-4 mr-2" />
              Admin Logins
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-[#0A0A0A] border border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#7a6200]" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-sm font-ubuntu">Total Users</p>
                      <p className="text-3xl font-bebas text-white">{analytics?.total_users || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#0A0A0A] border border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-[#7a6200]" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-sm font-ubuntu">Total Events</p>
                      <p className="text-3xl font-bebas text-white">{analytics?.total_events || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#0A0A0A] border border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#7a6200]" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-sm font-ubuntu">Partner Venues</p>
                      <p className="text-3xl font-bebas text-white">{analytics?.total_venues || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#0A0A0A] border border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#7a6200]" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-sm font-ubuntu">NDA Signatures</p>
                      <p className="text-3xl font-bebas text-white">{analytics?.total_ndas || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-[#0A0A0A] border border-white/10">
              <CardHeader>
                <CardTitle className="font-bebas text-xl tracking-wider text-white">
                  REGISTERED USERS ({users.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[#A1A1AA]">Name</TableHead>
                      <TableHead className="text-[#A1A1AA]">Email</TableHead>
                      <TableHead className="text-[#A1A1AA]">Status</TableHead>
                      <TableHead className="text-[#A1A1AA]">Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user, i) => (
                      <TableRow key={i} className="border-white/5">
                        <TableCell className="text-white font-ubuntu">{user.full_name}</TableCell>
                        <TableCell className="text-[#A1A1AA]">{user.email}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs rounded ${
                            user.verification_status === 'verified' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {user.verification_status}
                          </span>
                        </TableCell>
                        <TableCell className="text-[#52525B]">
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NDA Tab */}
          <TabsContent value="nda">
            <Card className="bg-[#0A0A0A] border border-white/10">
              <CardHeader>
                <CardTitle className="font-bebas text-xl tracking-wider text-white">
                  NDA SUBMISSIONS ({ndaSubmissions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[#A1A1AA]">Name</TableHead>
                      <TableHead className="text-[#A1A1AA]">Email</TableHead>
                      <TableHead className="text-[#A1A1AA]">Signature</TableHead>
                      <TableHead className="text-[#A1A1AA]">IP Address</TableHead>
                      <TableHead className="text-[#A1A1AA]">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ndaSubmissions.map((nda, i) => (
                      <TableRow key={i} className="border-white/5">
                        <TableCell className="text-white font-ubuntu">{nda.full_name}</TableCell>
                        <TableCell className="text-[#A1A1AA]">{nda.email}</TableCell>
                        <TableCell className="text-[#A1A1AA] italic">{nda.signature}</TableCell>
                        <TableCell className="text-[#52525B]">{nda.ip_address}</TableCell>
                        <TableCell className="text-[#52525B]">{nda.date} {nda.time}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact">
            <Card className="bg-[#0A0A0A] border border-white/10">
              <CardHeader>
                <CardTitle className="font-bebas text-xl tracking-wider text-white">
                  CONTACT SUBMISSIONS ({contactSubmissions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[#A1A1AA]">Name</TableHead>
                      <TableHead className="text-[#A1A1AA]">Email</TableHead>
                      <TableHead className="text-[#A1A1AA]">Subject</TableHead>
                      <TableHead className="text-[#A1A1AA]">Message</TableHead>
                      <TableHead className="text-[#A1A1AA]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contactSubmissions.map((contact, i) => (
                      <TableRow key={i} className="border-white/5">
                        <TableCell className="text-white font-ubuntu">{contact.name}</TableCell>
                        <TableCell className="text-[#A1A1AA]">{contact.email}</TableCell>
                        <TableCell className="text-[#A1A1AA]">{contact.subject}</TableCell>
                        <TableCell className="text-[#52525B] max-w-xs truncate">{contact.message}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400">
                            {contact.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin Logins Tab */}
          <TabsContent value="logins">
            <Card className="bg-[#0A0A0A] border border-white/10">
              <CardHeader>
                <CardTitle className="font-bebas text-xl tracking-wider text-white">
                  ADMIN LOGIN HISTORY ({adminLogins.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[#A1A1AA]">Access Type</TableHead>
                      <TableHead className="text-[#A1A1AA]">IP Address</TableHead>
                      <TableHead className="text-[#A1A1AA]">Date</TableHead>
                      <TableHead className="text-[#A1A1AA]">Time</TableHead>
                      <TableHead className="text-[#A1A1AA]">Timezone</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adminLogins.map((login, i) => (
                      <TableRow key={i} className="border-white/5">
                        <TableCell>
                          <span className="px-2 py-1 text-xs rounded bg-[#7a6200]/20 text-[#C6A300]">
                            {login.access_type}
                          </span>
                        </TableCell>
                        <TableCell className="text-white font-mono">{login.ip_address}</TableCell>
                        <TableCell className="text-[#A1A1AA]">{login.date}</TableCell>
                        <TableCell className="text-[#A1A1AA]">{login.time}</TableCell>
                        <TableCell className="text-[#52525B]">{login.timezone}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CRMPage;
